/**
 * TargetsTab — Target vs Achievement scorecard for Management Hub.
 *
 * Manager+ view: shows team scorecard with revenue / deals / pipeline / calls /
 * emails / meetings — target, actual, % achievement, status badge.
 *
 * Click "Set targets" on any row to open the editor modal.
 *
 * Indian Fiscal Year aware: defaults to current fiscal quarter (Apr–Mar).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Pill, WidgetCard, PrimaryButton } from '@/components/ui/native';
import { Target as TargetIcon, X, Search } from 'lucide-react';

// ─── Currency formatter (INR) ────────────────────────────────────────────
function fmtINR(n) {
  if (n == null || n === 0) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n}`;
}

// ─── Status colour mapping ───────────────────────────────────────────────
const STATUS_TONE = {
  smashed: 'emerald',
  on_track: 'blue',
  behind: 'amber',
  blown: 'rose',
};
const STATUS_LABEL = {
  smashed: 'Smashed',
  on_track: 'On track',
  behind: 'Behind',
  blown: 'At risk',
};

// ─── Progress bar ────────────────────────────────────────────────────────
function ProgressBar({ pct, status }) {
  if (pct == null) return <span className="text-xs text-slate-400">no target</span>;
  const w = Math.min(100, Math.max(0, pct));
  const colour = status === 'smashed' ? 'bg-emerald-500'
    : status === 'on_track' ? 'bg-blue-500'
    : status === 'behind' ? 'bg-amber-500'
    : 'bg-rose-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden min-w-[60px]">
        <div className={`h-full ${colour}`} style={{ width: `${w}%` }} />
      </div>
      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 tabular-nums w-10 text-right">{Math.round(pct)}%</span>
    </div>
  );
}

// ─── Metric cell — "actual / target" with progress bar ───────────────────
function MetricCell({ m, isCurrency = false, unit }) {
  const fmt = (v) => isCurrency ? fmtINR(v) : Math.round(v || 0).toLocaleString('en-IN');
  return (
    <div className="space-y-1">
      <div className="text-xs text-slate-700 dark:text-slate-300 tabular-nums">
        <span className="font-semibold">{fmt(m.actual)}</span>
        <span className="text-slate-400"> / {fmt(m.target)}{unit ? ` ${unit}` : ''}</span>
      </div>
      <ProgressBar pct={m.pct} status={m.status} />
    </div>
  );
}

// ─── Set Targets modal ───────────────────────────────────────────────────
function SetTargetsModal({ rep, period, onClose, onSaved }) {
  const [form, setForm] = useState({
    revenue_target: rep?.metrics?.revenue?.target || 0,
    deals_target: rep?.metrics?.deals?.target || 0,
    pipeline_target: rep?.metrics?.pipeline?.target || 0,
    calls_per_day: rep?.metrics?.calls?.target || 0,
    emails_per_day: rep?.metrics?.emails?.target || 0,
    meetings_per_week: rep?.metrics?.meetings?.target || 0,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      await axios.post(`${API}/manage/team-targets`, {
        rep_id: rep.rep_id,
        period,
        ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, Number(v) || 0])),
      });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to save targets');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="set-targets-modal">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <TargetIcon className="w-4 h-4 text-amber-500" /> Set targets
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">{rep.rep_name} · {rep.rep_role} · period {period}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded" data-testid="close-targets-modal">
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <FormField label="Revenue target (₹)" value={form.revenue_target} onChange={(v) => update('revenue_target', v)} testid="target-revenue" />
          <FormField label="Deals to win" value={form.deals_target} onChange={(v) => update('deals_target', v)} testid="target-deals" />
          <FormField label="Pipeline created (₹)" value={form.pipeline_target} onChange={(v) => update('pipeline_target', v)} testid="target-pipeline" />
          <FormField label="Calls per day" value={form.calls_per_day} onChange={(v) => update('calls_per_day', v)} testid="target-calls" />
          <FormField label="Emails per day" value={form.emails_per_day} onChange={(v) => update('emails_per_day', v)} testid="target-emails" />
          <FormField label="Meetings per week" value={form.meetings_per_week} onChange={(v) => update('meetings_per_week', v)} testid="target-meetings" />
        </div>
        {err && <div className="px-5 pb-2 text-sm text-rose-600">{err}</div>}
        <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 rounded-lg" data-testid="cancel-targets">Cancel</button>
          <PrimaryButton onClick={submit} disabled={busy} data-testid="save-targets">{busy ? 'Saving…' : 'Save targets'}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">{label}</label>
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400"
        data-testid={testid}
      />
    </div>
  );
}

// ─── Main TargetsTab ─────────────────────────────────────────────────────
export default function TargetsTab() {
  const [periodOpts, setPeriodOpts] = useState({ quarters: [], months: [], current: 'FY2026-Q1', fiscal_year: 'FY2026' });
  const [period, setPeriod] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  // ─── Filters ──────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | smashed | on_track | behind | blown | no_target

  // load period options once
  useEffect(() => {
    axios.get(`${API}/manage/targets/periods`)
      .then((r) => {
        setPeriodOpts(r.data);
        setPeriod((p) => p || r.data.current);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!period) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/manage/targets/team-scorecard?period=${period}`);
      setRows(r.data.rows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  // ─── Apply filters (rep name + status) ──────────────────────────────────
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !(r.rep_name || '').toLowerCase().includes(q) && !(r.rep_role || '').toLowerCase().includes(q)) {
        return false;
      }
      if (statusFilter === 'all') return true;
      if (statusFilter === 'no_target') return !r.has_targets;
      return r.has_targets && r.metrics?.revenue?.status === statusFilter;
    });
  }, [rows, search, statusFilter]);

  // ─── Aggregate KPIs at the top ──────────────────────────────────────────
  const totals = rows.reduce((a, r) => ({
    revenue_actual: a.revenue_actual + (r.metrics.revenue.actual || 0),
    revenue_target: a.revenue_target + (r.metrics.revenue.target || 0),
    deals_actual: a.deals_actual + (r.metrics.deals.actual || 0),
    deals_target: a.deals_target + (r.metrics.deals.target || 0),
    on_track: a.on_track + (r.metrics.revenue.status === 'smashed' || r.metrics.revenue.status === 'on_track' ? 1 : 0),
    blown: a.blown + (r.metrics.revenue.status === 'blown' ? 1 : 0),
    has_targets: a.has_targets + (r.has_targets ? 1 : 0),
  }), { revenue_actual: 0, revenue_target: 0, deals_actual: 0, deals_target: 0, on_track: 0, blown: 0, has_targets: 0 });

  const teamRevPct = totals.revenue_target > 0 ? Math.round(totals.revenue_actual * 100 / totals.revenue_target) : null;

  return (
    <div className="space-y-5" data-testid="targets-tab">
      {/* ── Hint banner: explain how to set targets ──────────────────────── */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 flex items-start gap-3">
        <TargetIcon className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm text-blue-900 dark:text-blue-200">
          <span className="font-medium">Set targets per rep:</span>
          <span className="ml-1 text-blue-800 dark:text-blue-300">click any row in the scorecard below — or use the button at the end of each row — to set Revenue, Deals, Pipeline, Calls, Emails, and Meeting goals for the selected period.</span>
        </div>
      </div>

      {/* ── Header strip: period selector + summary ────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <label className="block text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Period</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 min-w-[180px]"
            data-testid="period-selector"
          >
            <optgroup label="Fiscal Year">
              <option value={periodOpts.fiscal_year}>{periodOpts.fiscal_year} (full year)</option>
            </optgroup>
            <optgroup label="Quarters">
              {periodOpts.quarters?.map((q) => <option key={q} value={q}>{q}</option>)}
            </optgroup>
            <optgroup label="Months">
              {periodOpts.months?.map((m) => <option key={m} value={m}>{m}</option>)}
            </optgroup>
          </select>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-2.5">
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Team revenue</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">{fmtINR(totals.revenue_actual)} <span className="text-xs font-normal text-slate-400">/ {fmtINR(totals.revenue_target)}</span></div>
            {teamRevPct != null && <div className="text-xs text-slate-500">{teamRevPct}% achieved</div>}
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-2.5">
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Reps with targets</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">{totals.has_targets} <span className="text-xs font-normal text-slate-400">/ {rows.length}</span></div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-2.5">
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">On track / At risk</div>
            <div className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">
              <span className="text-emerald-600">{totals.on_track}</span> <span className="text-slate-400">/</span> <span className="text-rose-600">{totals.blown}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Scorecard Table ─────────────────────────────────────────────── */}
      <WidgetCard
        title={`Team scorecard · ${period} · ${filteredRows.length}${filteredRows.length !== rows.length ? ` of ${rows.length}` : ''} reps`}
        tooltip="Smashed ≥100% · On track ≥75% · Behind ≥50% · At risk <50%"
        headerRight={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search rep…"
                className="pl-8 pr-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400 w-44"
                data-testid="targets-search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2.5 py-1.5 text-xs border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-400"
              data-testid="targets-status-filter"
            >
              <option value="all">All statuses</option>
              <option value="smashed">Smashed</option>
              <option value="on_track">On track</option>
              <option value="behind">Behind</option>
              <option value="blown">At risk</option>
              <option value="no_target">No target set</option>
            </select>
            {(search || statusFilter !== 'all') && (
              <button
                onClick={() => { setSearch(''); setStatusFilter('all'); }}
                className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 px-2 py-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
                data-testid="targets-clear-filters"
              >
                Clear
              </button>
            )}
          </div>
        }
      >
        {loading ? (
          <div className="text-sm text-slate-400 py-6 text-center">Loading scorecard…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">No subordinates found.</div>
        ) : filteredRows.length === 0 ? (
          <div className="text-sm text-slate-400 py-6 text-center">No reps match the current filters.</div>
        ) : (
          <div className="overflow-x-auto -mx-4">
            <table className="w-full text-sm min-w-[1180px]" data-testid="scorecard-table">
              <thead>
                <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/60">
                  <th className="text-left px-4 py-2 whitespace-nowrap">Rep</th>
                  <th className="text-left px-3 py-2 whitespace-nowrap">Status</th>
                  <th className="text-left px-3 py-2 min-w-[160px] whitespace-nowrap">Revenue</th>
                  <th className="text-left px-3 py-2 min-w-[140px] whitespace-nowrap">Deals</th>
                  <th className="text-left px-3 py-2 min-w-[160px] whitespace-nowrap">Pipeline</th>
                  <th className="text-left px-3 py-2 min-w-[140px] whitespace-nowrap">Calls/day</th>
                  <th className="text-left px-3 py-2 min-w-[140px] whitespace-nowrap">Emails/day</th>
                  <th className="text-left px-3 py-2 min-w-[140px] whitespace-nowrap">Meetings/wk</th>
                  <th className="text-right px-4 py-2 whitespace-nowrap sticky right-0 bg-slate-50/95 dark:bg-slate-800/95 backdrop-blur shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.18)]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {filteredRows.map((r) => {
                  const status = r.metrics.revenue.status;
                  return (
                    <tr
                      key={r.rep_id}
                      className="group hover:bg-slate-50 dark:hover:bg-slate-800/40 cursor-pointer"
                      onClick={() => setEditing(r)}
                      data-testid={`scorecard-row-${r.rep_id}`}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-slate-900 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400">{r.rep_name}</div>
                        <div className="text-xs text-slate-500 capitalize">{r.rep_role?.replace(/_/g, ' ')}</div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {r.has_targets && status ? (
                          <Pill tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Pill>
                        ) : (
                          <span className="text-xs text-slate-400 italic">no target set</span>
                        )}
                      </td>
                      <td className="px-3 py-3"><MetricCell m={r.metrics.revenue} isCurrency /></td>
                      <td className="px-3 py-3"><MetricCell m={r.metrics.deals} /></td>
                      <td className="px-3 py-3"><MetricCell m={r.metrics.pipeline} isCurrency /></td>
                      <td className="px-3 py-3"><MetricCell m={r.metrics.calls} /></td>
                      <td className="px-3 py-3"><MetricCell m={r.metrics.emails} /></td>
                      <td className="px-3 py-3"><MetricCell m={r.metrics.meetings} /></td>
                      <td className="px-4 py-3 text-right whitespace-nowrap sticky right-0 bg-white dark:bg-slate-800 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/40 shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.18)]">
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditing(r); }}
                          className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-md whitespace-nowrap transition-colors border ${
                            r.has_targets
                              ? 'text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700'
                              : 'text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800/60 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                          }`}
                          data-testid={`edit-targets-${r.rep_id}`}
                        >
                          {r.has_targets ? 'Edit' : '+ Set targets'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </WidgetCard>

      {editing && (
        <SetTargetsModal
          rep={editing}
          period={period}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
